import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Trophy } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={loc.state?.from || "/"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center">
              <Trophy className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              GameLink<span className="text-blue-600">OS</span>
            </div>
          </div>
          <div className="text-sm text-slate-500 uppercase tracking-widest">Internal Operations</div>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <h1 className="text-xl font-bold text-slate-900 mb-1">Sign in</h1>
            <p className="text-sm text-slate-500 mb-6">Manage venues, events, tasks and finances.</p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email" type="email" required
                  data-testid="login-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password" type="password" required
                  data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button
                type="submit" disabled={busy}
                data-testid="login-submit-button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {busy ? "Signing in..." : "Sign in"}
              </Button>
              <div className="text-center pt-2">
                <a
                  href="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot password?
                </a>
              </div>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} GameLink · Internal use only
        </p>
      </div>
    </div>
  );
}
